import { useGoogleLogin } from '@react-oauth/google';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import * as ClassroomsAPI from 'src/api/Classrooms';
import { GoogleClassroomImg } from 'src/assets/icons';
import { ImportGoogleClassImg, LogoBlackImg } from 'src/assets/images';
import { SelectList } from 'src/components';
import { Button } from 'src/components/buttons';
import Input from 'src/components/inputs/Input';
import { Description, Title } from 'src/components/typography';
import { useClassroomStore } from 'src/hooks/zustand/classrooms';
import { lang } from 'src/lang';
import { track, Events } from 'src/utils/analytics';

import ClickToCopy from 'src/components/ClickToCopy';
import { useAuthenticatedUser } from 'src/hooks/user';
import BaseDialog, { DialogContent, DialogTitle } from '../BaseDialog';
import JoinCodeDialog from './JoinCodeDialog';
import {
  CreateClassroomBox,
  OptionImport,
  OptionImportList,
} from './styles';

const {
  Marker,
  OptionLabel,
  ListOption,
  Option,
} = SelectList;

const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/classroom.courses.readonly'
  + ' https://www.googleapis.com/auth/classroom.profile.emails'
  + ' https://www.googleapis.com/auth/classroom.rosters.readonly';

function CreateClassroomDialog({
  hasGoogleRedirect,
  setTab,
  isOpen,
  onOpen,
  onClose,
  fromAssignment = false,
  onCloseCallback,
  showClassroomList = false,
}: {
  hasGoogleRedirect?: boolean,
  setTab?: (value: number) => void,
  isOpen: boolean;
  onOpen: () => void,
  onClose: (...args: any[]) => void;
  fromAssignment?: boolean,
  onCloseCallback?: () => void,
  showClassroomList?: boolean,
}) {
  const user = useAuthenticatedUser();
  const classrooms = useClassroomStore((state) => state.classrooms);
  const loadClassrooms = useClassroomStore((state) => state.loadClassrooms);

  const [searchParams, setSearchParams] = useSearchParams();

  // Used to navigate to the classroom screen after import from google
  const [newClassroomId, setNewClassroomId] = useState(null);
  const [currentStep, setCurrentStep] = useState(showClassroomList ? -1 : 0);

  const [selectedClassrooms, setSelectedClassrooms] = useState<any>([]);
  const [classroomList, setClassroomList] = useState<any>([]);
  const [classroom, setClassroom] = useState('');
  const [subject, setSubject] = useState('');
  const [section, setSection] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);

  const [code, setCode] = useState('');
  const [classroomId, setClassroomId] = useState<string | null>(null);

  const { state, pathname } = useLocation();

  const navigate = useNavigate();

  // Reset step when dialog opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(showClassroomList ? -1 : 0);
    }
  }, [isOpen, showClassroomList]);

  // Clean up when dialog closes
  useEffect(() => {
    if (isOpen || !!state) return;
    setTimeout(() => {
      setSelectedClassrooms([]);
      setClassroomList([]);
      setClassroom('');
      setSubject('');
      setSection('');
    }, 100);
  }, [isOpen]);

  const handleClose = () => {
    if (setTab) setTab(2);
    loadClassrooms();
    if (fromAssignment && onCloseCallback) {
      onCloseCallback();
    }

    if (currentStep === 4) navigate(`/classroom/${newClassroomId}`);
    onClose();
  };

  const importData = async () => {
    setLoading(true);
    try {
      const response = await ClassroomsAPI.saveFromGoogle({
        classrooms: selectedClassrooms.map((e: any) => e.id),
      });
      if (response.status === 200) {
        track(Events.ImportFromGoogleClassroom, {
          classrooms_imported: response.data.data.length,
          source: 'google_classroom',
        });
        const listOfNewClassrooms = response.data.data.map((c: any) => c.id);
        const responseClassrooms = await ClassroomsAPI.getAll();
        if (response.status === 200) {
          // Get first classroom imported to navigate when click "View your class"
          setNewClassroomId(listOfNewClassrooms[0]);
          setClassroomList(
            responseClassrooms.data.data.map((c: any) => ({
              ...c,
              isSyncing: listOfNewClassrooms.includes(c.id),
            })),
          );
        }
        response.data.data.forEach((e: any) => (
          ClassroomsAPI.syncWithGoogle(e.id)
        ));
        // If the classroom creation was triggered from assignment creation,
        // the last step doesn't need to appear, as it shows a link to open classroom screen
        if (!pathname.includes('/assignment')) setCurrentStep(4);
        else handleClose();
      }
    } catch (error) { }
    setLoading(false);
  };

  const getGoogleClassrooms = useGoogleLogin({
    flow: 'auth-code',
    scope: GOOGLE_SCOPES,
    ux_mode: 'redirect',
    redirect_uri: `${window.location.origin}/classrooms/providers/oauth/google`,
    select_account: false,
    hint: user?.email || undefined,
  });

  useEffect(() => {
    setErrorMessage(false);
    if (searchParams.get('error') && hasGoogleRedirect) {
      onOpen();
      if (state?.permissionError) {
        setCurrentStep(3);
        setErrorMessage(true);
      }
      setSearchParams('');
    } else if (searchParams.get('loadClassrooms') && hasGoogleRedirect) {
      onOpen();
      if (state?.classroomList) {
        setCurrentStep(3);
        setClassroomList(state?.classroomList || []);
      }
      if (!fromAssignment) setSearchParams('');
    }
  }, []);

  const handleSelect = (item: any) => {
    if (selectedClassrooms.find((e: any) => item.id === e.id)) {
      setSelectedClassrooms(
        selectedClassrooms.filter((e: any) => item.id !== e.id),
      );
    } else {
      setSelectedClassrooms([...selectedClassrooms, item]);
    }
  };

  const submitForm = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    const response = await ClassroomsAPI.createClassroom({
      name: classroom,
      section: section || undefined,
      subject: subject || undefined,
    });
    if (response.status === 201) {
      setCode(response.data.data.join_code);
      setClassroomId(response.data.data.id);
      setCurrentStep(2);
      loadClassrooms();
      track(Events.CreateClassroom, {
        has_section: !!section,
        has_subject: !!subject,
      });
    }
    setLoading(false);
  };

  const enableNextStep = useMemo(() => classroom.length > 0, [classroom]);

  const handleBackButton = () => {
    setCurrentStep(showClassroomList ? -1 : 0);
    setClassroom('');
    setSubject('');
    setSection('');
  };

  const getDialogTitle = () => {
    if (errorMessage) {
      return lang('classroom.create_classroom_dialog.error.title');
    }

    if (currentStep === 3) {
      if (loading) {
        return lang('classroom.create_classroom_dialog.step_3.loading.title');
      }
      if (classroomList?.length === 0) {
        return lang('classroom.create_classroom_dialog.step_3.no_classroom');
      }

      return lang('classroom.create_classroom_dialog.step_3.choose_classroom');
    }

    if (currentStep === 4) {
      return lang('classroom.create_classroom_dialog.step_4.title');
    }

    return lang(`classroom.create_classroom_dialog.step_${currentStep}.title`);
  };

  if (currentStep === 2) {
    return (
      <JoinCodeDialog
        onClose={handleClose}
        isOpen={isOpen}
        code={code}
        buttonLabel={
          fromAssignment
            ? lang('classroom.create_classroom_dialog.step_2.submit.from_assignment')
            : lang('classroom.create_classroom_dialog.step_2.submit.from_classroom')
        }
        classroomId={classroomId}
        showBottomAction
        fromAssignment={fromAssignment}
      />
    );
  }

  return (
    <BaseDialog
      size="medium"
      onClose={onClose}
      open={isOpen}
      showBackButton={currentStep > (showClassroomList ? -1 : 0) && currentStep < 4 && !loading}
      onBackButton={handleBackButton}
    >
      <DialogContent>
        {currentStep === 3 && loading && (
          <div style={{ display: 'flex', alignItems: 'center', paddingBlock: '40px' }}>
            <img style={{ margin: 'auto' }} src={ImportGoogleClassImg} alt="" />
          </div>
        )}
        <DialogTitle>{currentStep === -1 ? 'Your Classrooms' : getDialogTitle()}</DialogTitle>

        {currentStep === -1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.25rem 0 0.75rem' }}>
              <Description>
                Send your students to <b style={{ color: '#163567' }}>pressto.fun</b> to join!
              </Description>
              <Button
                label="+ Create another classroom"
                onClick={() => setCurrentStep(0)}
                style={{ whiteSpace: 'nowrap', fontSize: '0.8rem', padding: '8px 16px' }}
              />
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {classrooms.map((c: any) => {
                const joinUrl = `${window.location.protocol}//${window.location.host}/code/${c.join_code}`;
                return (
                  <div
                    key={c.id}
                    style={{
                      border: '2px solid #1e1e1e',
                      borderRadius: '8px',
                      padding: '1.25rem',
                      background: '#faf7f0',
                      boxShadow: '3px 3px 0px #1e1e1e',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <span style={{ fontFamily: '"Georgia", serif', fontWeight: 700, fontSize: '1.1rem', color: '#1a1a1a' }}>{c.name}</span>
                      <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.75rem', color: '#888', fontWeight: 600 }}>{c.students?.length ?? 0} students</span>
                    </div>
                    <ClickToCopy text={c.join_code}>
                      <div style={{
                        background: '#fff',
                        border: '2px solid #163567',
                        borderRadius: '8px',
                        padding: '0.75rem 1rem',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 100ms ease-in-out',
                      }}>
                        <div style={{ fontSize: '0.65rem', color: '#163567', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'Montserrat, sans-serif', fontWeight: 700 }}>
                          Join Code
                        </div>
                        <div style={{ fontSize: '2rem', fontWeight: 800, fontFamily: 'monospace', letterSpacing: '0.15em', color: '#163567' }}>
                          {c.join_code}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '2px', fontFamily: 'Montserrat, sans-serif' }}>
                          Click to copy
                        </div>
                      </div>
                    </ClickToCopy>
                    <ClickToCopy text={joinUrl}>
                      <div style={{ fontSize: '0.75rem', color: '#163567', marginTop: '0.75rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'Montserrat, sans-serif' }}>
                        {joinUrl}
                      </div>
                    </ClickToCopy>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {currentStep === 0 && (
          <OptionImportList
            sx={{
              margin: '2rem 2rem 1rem 4rem',
            }}
          >
            <OptionImport
              onClick={() => {
                if (!loading) {
                  // Store assignment url with it's id in order to go back to this
                  // screen after importing the classrooms
                  if (pathname.includes('/assignment')) {
                    localStorage.setItem('assignmentUrl', pathname);
                  }
                  track(Events.ImportFromGoogleClassroom, { source: 'create_classroom_dialog' });
                  getGoogleClassrooms();
                }
              }}
              disabled={loading}
            >
              <img src={GoogleClassroomImg} alt="" height={50} />
              <Title size="small" lineHeight="30px">
                {lang('classroom.create_classroom_dialog.step_0.import')}
              </Title>
              <Description>
                {lang('classroom.create_classroom_dialog.step_0.sync')}
              </Description>
            </OptionImport>
            <OptionImport
              onClick={() => !loading && setCurrentStep(1)}
              disabled={loading}
            >
              <img src={LogoBlackImg} alt="" height={50} />
              <Title size="small" lineHeight="30px" whiteSpace="pre-line">
                {lang('classroom.create_classroom_dialog.step_0.create')}
              </Title>
              <Description>
                {lang('classroom.create_classroom_dialog.step_0.invite')}
              </Description>
            </OptionImport>
          </OptionImportList>
        )}

        {currentStep === 1 && (
          <CreateClassroomBox>
            <Input
              placeholder={lang(
                'classroom.create_classroom_dialog.step_1.classroom_placeholder',
              )}
              value={classroom}
              required
              onChange={(event: any) => setClassroom(event.target.value)}
              maxLength={25}
              disabled={loading}
            />
            <Input
              placeholder={lang(
                'classroom.create_classroom_dialog.step_1.subject_placeholder',
              )}
              value={subject}
              onChange={(event: any) => setSubject(event.target.value)}
              maxLength={25}
              disabled={loading}
            />
            <Input
              placeholder={lang(
                'classroom.create_classroom_dialog.step_1.section_placeholder',
              )}
              value={section}
              onChange={(event: any) => setSection(event.target.value)}
              maxLength={25}
              disabled={loading}
            />
            <Button
              disabled={!enableNextStep || loading}
              label={lang('classroom.create_classroom_dialog.step_1.submit')}
              onClick={submitForm}
            />
          </CreateClassroomBox>
        )}

        {currentStep === 3 && loading && (
          <Description>
            {lang('classroom.create_classroom_dialog.step_3.loading.description')}
          </Description>
        )}

        {currentStep === 3 && errorMessage && !loading && (
          <>
            <Description align="center">
              {lang('classroom.create_classroom_dialog.error.subtitle')}
            </Description>
            <Button
              label={lang('classroom.create_classroom_dialog.go_back')}
              onClick={handleBackButton}
              style={{
                margin: '3rem auto 0',
              }}
            />
          </>
        )}

        {currentStep === 3 && !errorMessage && classroomList?.length === 0 && !loading && (
          <>
            <Description>
              {lang('classroom.create_classroom_dialog.step_3.create')}
            </Description>
            <Button
              label={lang('classroom.create_classroom_dialog.go_back')}
              onClick={handleBackButton}
              style={{
                margin: '3rem auto 0',
              }}
            />
          </>
        )}

        {currentStep === 3 && !errorMessage && classroomList?.length > 0 && !loading && (
          <>
            <Description align="center">
              {lang(
                'classroom.create_classroom_dialog.step_3.classroom_count',
                { count: classroomList?.length ?? 0 },
              )}
            </Description>
            <ListOption>
              {classroomList?.map((item: any) => (
                <Option
                  key={item.id}
                  onClick={() => !item.imported_at && handleSelect(item)}
                  disabled={item.imported_at}
                >
                  <Marker
                    selected={
                      selectedClassrooms.find((e: any) => e.id === item.id)
                      || item.imported_at
                    }
                  />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      margin: '0 .5rem',
                    }}
                  >
                    <img src={GoogleClassroomImg} alt="" width={40} />
                  </div>
                  <OptionLabel>
                    <Title size="small" lineHeight="20px">
                      {item.name}
                      {item.imported_at && (
                        <span
                          style={{
                            marginLeft: 15,
                            fontWeight: 'normal',
                            fontSize: '0.75rem',
                          }}
                        >
                          {lang(
                            'classroom.create_classroom_dialog.step_3.imported',
                            { date: dayjs(item.imported_at).fromNow() },
                          )}
                        </span>
                      )}
                    </Title>
                    <Description>{item.section}</Description>
                  </OptionLabel>
                </Option>
              ))}
            </ListOption>
            <Button
              label={lang(
                `classroom.create_classroom_dialog.step_3.${fromAssignment ? 'submit_from_assignment' : 'submit'
                }`,
              )}
              onClick={importData}
              disabled={selectedClassrooms.length === 0 || loading}
              style={{
                margin: '3rem auto 0',
              }}
            />
          </>
        )}

        {currentStep === 4 && (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            <Description>
              {lang('classroom.create_classroom_dialog.step_4.description.line_1')}
            </Description>
            <Description>
              {lang('classroom.create_classroom_dialog.step_4.description.line_2.part_1')}
              <ClickToCopy text={window.location.origin}>
                <b>
                  {window.location.origin}
                </b>
              </ClickToCopy>
              {lang('classroom.create_classroom_dialog.step_4.description.line_2.part_2')}
            </Description>
            <Button
              label={lang('classroom.create_classroom_dialog.step_4.submit')}
              onClick={handleClose}
              style={{
                margin: '1rem auto 0',
                padding: '10px 80px',
              }}
            />
          </div>
        )}
      </DialogContent>
    </BaseDialog>
  );
}

export default CreateClassroomDialog;
